import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { strings } from '@singparty/shared';
import { useGuests, useMe, useQueue, useSession } from '@/stores/sessionStore';
import { QueueList } from '@/components/QueueList';
import { ConnectionPill } from '@/components/ConnectionPill';
import { Button } from '@/components/ui/Button';
import { Logo } from '@/components/ui/Logo';

export const Route = createFileRoute('/s/$sid/')({
  component: GuestHome,
});

function GuestHome() {
  const { sid } = Route.useParams();
  const navigate = useNavigate();
  const session = useSession();
  const me = useMe();
  const guests = useGuests();
  const queue = useQueue();

  const myName = me?.guestId ? guests.find((g) => g.id === me.guestId)?.displayName : undefined;
  const playing = queue.find((q) => q.status === 'playing');
  const myPending = queue.filter((q) => q.status === 'queued' && q.requestedBy.id === me?.guestId);
  const settings = session?.settings;

  return (
    <div className="mx-auto max-w-[560px] px-4 py-6 pb-28">
      <Logo className="mb-4" />
      <ConnectionPill />
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-extrabold">{session?.name ?? strings.brand}</h1>
          {myName && <p className="text-sm text-text-secondary">You're {myName}</p>}
        </div>
      </header>

      <section className="mb-6 rounded-card border border-border bg-linear-to-br from-surface to-surface-2 p-6 text-center">
        {playing ? (
          <>
            <p className="text-xs uppercase tracking-wide text-accent">{strings.guestHome.nowPlaying}</p>
            <p className="mt-1 text-lg font-bold">{playing.song.title}</p>
            <p className="text-sm text-text-secondary">🎤 {playing.singerLabel}</p>
          </>
        ) : (
          <p className="text-lg font-semibold">{strings.guestHome.queueOpen}</p>
        )}
      </section>

      {myPending.length > 0 && (
        <div className="mb-4 rounded-pill bg-primary/15 px-4 py-2 text-center text-sm text-primary">
          {strings.guestHome.myTurn(queue.filter((q) => q.status === 'queued').findIndex((q) => q.id === myPending[0]!.id) + 1, null)}
        </div>
      )}

      <section className="mb-6">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-secondary">
          In the room · {guests.length}
        </h2>
        <div className="flex flex-wrap gap-2">
          {guests.map((g) => (
            <span key={g.id} className="rounded-pill bg-surface-2 px-3 py-1 text-sm">
              {g.isHost ? '👑 ' : '🎤 '}
              {g.displayName}
            </span>
          ))}
        </div>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-secondary">Queue</h2>
        <QueueList
          sid={sid}
          role="guest"
          myGuestId={me?.guestId}
          guestRemoveOwn={settings?.guest_remove_own ?? true}
        />
      </section>

      <div className="fixed inset-x-0 bottom-0 border-t border-border bg-bg/95 px-4 py-3 pb-safe backdrop-blur">
        <div className="mx-auto max-w-[560px]">
          <Button size="lg" className="w-full" onClick={() => navigate({ to: '/s/$sid/add', params: { sid } })}>
            {strings.guestHome.addSong}
          </Button>
        </div>
      </div>
    </div>
  );
}
